import { ReactNode, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { UserButton, useUser, useAuth } from '@clerk/clerk-react'
import { Menu, X, HelpCircle } from 'lucide-react'
import { DsIsland, useAppTheme } from '@/design-system/island'
import '@/design-system/shell.css'
import { SideGroup, SideLink } from '@/design-system/app'
import { BrandMark } from '@/design-system/primitives'
import {
  APP_NAV,
  APP_NAV_FOOT,
  APP_NAV_READING,
  APP_NAV_SETUP,
  AppNavItem,
  visibleEntries,
} from './AppNav'
import AppFooter from './AppFooter'
import ThemeToggle from './ThemeToggle'
import ResourcesDropdown from './ResourcesDropdown'
import TeamSwitcher from './TeamSwitcher'
import NotificationDropdown from './NotificationDropdown'
import { useIsAdmin } from '@/hooks/useIsAdmin'
import { useChiefAccess } from '@/hooks/useChiefAccess'
import { useGreeting } from '@/hooks/useGreeting'
import { useDomain } from '@/hooks/useDomain'
import { useTierStatus } from '@/hooks/useTierStatus'
import { HeaderSkeleton } from './skeletons/HeaderSkeleton'
import PastDueBanner from './PastDueBanner'
import RedesignBanner from './RedesignBanner'
import MonitoringPickerBanner from './MonitoringPickerBanner'
// DomainPicker removed — domain context now comes from URL path
import ServiceStatusBanner from './ServiceStatusBanner'
import SidebarDomainList from './SidebarDomainList'
import AddDomainDialog from './domains/AddDomainDialog'
import SupportModal from './SupportModal'
/* The door: a HubSpot-path account waiting on its trial is turned to the report. */
import OnboardingStandingGate from './OnboardingStandingGate'

interface LayoutProps {
  children: ReactNode
}

export default function Layout({ children }: LayoutProps) {
  const location = useLocation()
  const theme = useAppTheme()
  const { isLoaded: isUserLoaded } = useUser()
  const { isLoaded: isAuthLoaded } = useAuth()
  const isAdmin = useIsAdmin()
  const { state: chiefState, isLoading: chiefLoading } = useChiefAccess()
  // Visible to anyone who should see the pitch, not only to people already in.
  // isLoading is excluded so the item never flashes in and out: Layout renders
  // on every authenticated page.
  const showChiefNav = !chiefLoading && chiefState !== 'hidden'
  const { greeting } = useGreeting()
  const { domains, isLoadingDomains, hasDomains } = useDomain()
  const tierStatus = useTierStatus()
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [showSupportModal, setShowSupportModal] = useState(false)
  /*
    Mounted here rather than inside the site list. On a phone that list is
    inside the drawer, and the drawer closes the moment you press "Add a site":
    a dialog owned by the list would be unmounted by the very click that opened
    it. This outlives both, and one instance serves the rail and the drawer.
  */
  const [showAddSite, setShowAddSite] = useState(false)

  /* One list, filtered once, rendered by the rail and by the drawer. */
  const pathname = location.pathname
  const closeMenu = () => setIsMobileMenuOpen(false)
  const seenBy = { showChief: showChiefNav, isAdmin }
  const nav = visibleEntries(APP_NAV, seenBy)
  const setup = visibleEntries(APP_NAV_SETUP, seenBy)
  const foot = visibleEntries(APP_NAV_FOOT, seenBy)

  // Check if user and auth are still loading
  const isLoading = !isUserLoaded || !isAuthLoaded
  const headerPlanDisplayName =
    tierStatus.displayName ||
    (tierStatus.tier === 'legacy_pro'
      ? 'Growth'
      : tierStatus.effectiveTier === 'free'
        ? 'Free Trial'
        : formatHeaderPlanName(tierStatus.effectiveTier || tierStatus.tier))
  const showHeaderPlanBadge = !tierStatus.isLoading && tierStatus.entitlements.isPaidSubscriber

  return (
    <OnboardingStandingGate>
    <div className="min-h-screen bg-background">
      {/* Header */}
      {/*
        The header is the `.ds` scope now, the same way `ds-masthead` is on
        the public side. It used to be a Tailwind bar (`bg-background/95`, a
        backdrop blur, `border-border`) wrapped around a `DsIsland`, which is
        what `panel={false}` was for: a transitional island waiting for its
        shell to convert. This is that conversion.
      */}
      <header className="ds ds-appbar" data-ds-theme={theme}>
        <div className="ds-appbar__in">
          <div className="ds-appbar__brand">
            <Link to="/dashboard" className="ds-appbar__mark" aria-label="SuperSchema dashboard">
              {/*
                The brand mark the rest of the site uses. What was here drew
                a different logo: an inline SVG of a bolt with a face, pulsing
                on a three-second loop forever, spinning a full turn when
                tapped and dropping a hardcoded purple glow on hover. A
                logo that breathes on every authenticated page is ambient
                motion on every screen at once, and the purple was not the
                accent this system has.
              */}
              <BrandMark className="ds-appbar__logo" size={31} />
              <span className="brand-wordmark ds-appbar__word" aria-hidden="true">
                <span className="brand-wordmark-super">Super</span>
                <span className="brand-wordmark-schema">Schema</span>
              </span>
              {showHeaderPlanBadge && (
                <span className="ds-appbar__plan">
                  {headerPlanDisplayName}
                </span>
              )}
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="ds-appbar__acts">
            {isLoading ? (
              <HeaderSkeleton />
            ) : (
              <>
                <ResourcesDropdown />
                <button
                  type="button"
                  onClick={() => setShowSupportModal(true)}
                  className="ds-appbar__btn"
                >
                  <HelpCircle className="h-4 w-4" />
                  Help
                </button>
                <TeamSwitcher />
                <span className="ds-appbar__hello">{greeting}</span>
                <NotificationDropdown />
                <ThemeToggle />
                <UserButton
                  afterSignOutUrl="/"
                  appearance={{
                    elements: {
                      avatarBox: "h-8 w-8"
                    }
                  }}
                />
              </>
            )}
          </div>

          {/* Mobile Controls */}
          <div className="ds-appbar__acts ds-appbar__acts--phone">
            <NotificationDropdown />
            <ThemeToggle />
            <UserButton
              afterSignOutUrl="/"
              appearance={{
                elements: {
                  avatarBox: "h-8 w-8"
                }
              }}
            />
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="ds-appbar__btn ds-appbar__btn--icon"
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <DsIsland panel={false} ground={false} className="ds-rail ds-rail--drawer md:hidden">
            <div className="ds-rail__body">
              {nav.map((entry) => (
                <AppNavItem key={entry.href} entry={entry} pathname={pathname} onGo={closeMenu} />
              ))}

              <SidebarDomainList
                domains={domains}
                isLoading={isLoadingDomains}
                hasDomains={hasDomains}
                onMobileClose={closeMenu}
                onAddSite={() => setShowAddSite(true)}
                variant="mobile"
              />

              <div className="ds-rail__rule" />

              {setup.map((entry) => (
                <AppNavItem key={entry.href} entry={entry} pathname={pathname} onGo={closeMenu} />
              ))}

              {/*
                Help and the guides live in the header on a wide screen, and
                the phone header has no room for them. They are here rather
                than in APP_NAV because that is exactly what they are: header
                items the drawer picks up, not rail items the rail forgot.
              */}
              <SideLink
                icon={<HelpCircle aria-hidden="true" />}
                onClick={() => {
                  closeMenu()
                  setShowSupportModal(true)
                }}
              >
                Help
              </SideLink>

              <div className="ds-rail__rule" />

              {foot.map((entry) => (
                <AppNavItem key={entry.href} entry={entry} pathname={pathname} onGo={closeMenu} />
              ))}

              <SideGroup>Reading</SideGroup>
              {APP_NAV_READING.map((item) => (
                <SideLink key={item.href} to={item.href} onClick={closeMenu} className="ds-rail__more">
                  {item.name}
                </SideLink>
              ))}
            </div>
          </DsIsland>
        )}
      </header>

      {/* The banners live INSIDE the fixed-height shell below, not between
          the header and it. Out there they added their own height to the
          document, which is already a full viewport tall (header + shell),
          so the window itself scrolled. The sidebar is fixed and the footer
          is in flow, so that scroll pulled the footer up off the bottom and
          slid it under the sidebar. Inside the shell they take their height
          out of the scrolling middle instead, where it belongs.

          The 1px is the header's bottom border: the bar is 64px and its rule
          makes it 65. Subtracting only the 64 left the document one pixel
          taller than the screen, which is enough for the window to scroll. */}
      <div className="flex flex-col h-[calc(100vh-4rem-1px)]">
        {/* Past Due Banner - shows when payment has failed */}
        <PastDueBanner />

        {/* Redesign Banner — says the product is being redressed, so a
            half-converted app reads as work in progress. Sits under the
            money and above the operational bars: it is the least urgent
            thing here and should never push a failed payment down. */}
        <RedesignBanner />

        {/* Monitoring Picker Banner — onboarding nudge for legacy customers
            to opt their verified URLs into active monitoring. Self-gates
            via the cap query and only renders when the team has 0 active
            monitors AND ≥1 verified URL. */}
        <MonitoringPickerBanner />

        {/* Service Status Banner - shows when third-party services are experiencing issues */}
        <ServiceStatusBanner />

        <div className="flex flex-1 overflow-hidden">
          {/* Sidebar */}
          <aside className="hidden md:flex md:w-[var(--app-rail)] md:flex-col md:fixed md:inset-y-16 md:left-0 md:bottom-12">
            <DsIsland panel={false} ground={false} className="ds-rail">
              <nav className="ds-rail__body" aria-label="Sections">
                {nav.map((entry) => (
                  <AppNavItem key={entry.href} entry={entry} pathname={pathname} />
                ))}

                <SidebarDomainList
                  domains={domains}
                  isLoading={isLoadingDomains}
                  hasDomains={hasDomains}
                  onAddSite={() => setShowAddSite(true)}
                  variant="desktop"
                />

                <div className="ds-rail__rule" />

                {setup.map((entry) => (
                  <AppNavItem key={entry.href} entry={entry} pathname={pathname} />
                ))}
              </nav>

              {/* Anchored below the list, however long the list gets. */}
              <div className="ds-rail__foot">
                {foot.map((entry) => (
                  <AppNavItem key={entry.href} entry={entry} pathname={pathname} />
                ))}
              </div>
            </DsIsland>
          </aside>

          {/* Main content */}
          {/* `relative` is what keeps the page inside this box. Anything
              absolutely positioned with nothing positioned around it (every
              screen-reader-only label is) answers to the document, not to
              this scroller: it is not clipped here and it does not scroll
              here, so a label far down a long page made the whole document
              that tall. The window then scrolled behind the page and brought
              the footer up over it. Measured on the site scan report: a
              7,015px document on a 768px screen without this, 768px with. */}
          <main className="relative flex-1 overflow-y-auto md:ml-[var(--app-rail)]">
            {children}
          </main>
        </div>

        {/* Footer */}
        <AppFooter />
      </div>

      <SupportModal
        isOpen={showSupportModal}
        onClose={() => setShowSupportModal(false)}
      />

      {/* Mounted only while open. It runs its own domain-limits query, and
          Layout is on every authenticated page: left mounted it would put a
          request behind every one of them. */}
      {showAddSite && (
        <AddDomainDialog isOpen onClose={() => setShowAddSite(false)} />
      )}
    </div>
    </OnboardingStandingGate>
  )
}

function formatHeaderPlanName(tier?: string | null) {
  switch (tier) {
    case 'essential':
      return 'Essential'
    case 'growth':
    case 'legacy_pro':
      return 'Growth'
    case 'portfolio':
      return 'Portfolio'
    case 'enterprise':
      return 'Enterprise'
    case 'free':
      return 'Free Trial'
    default:
      return 'Essential'
  }
}
